import httpStore from "@/api/httpStore.js";

export function loadMarketplacesItems(code) {
  return httpStore
    .get(`/marketplaces/single_product/${code}`)
    .then(res => res.data);
}

export function deleteMarketplaceItem(id) {
  return httpStore
    .delete(`/marketplaces/single_product_item?id=${id}`)
    .then(res => res.data);
}

export function updateMarketplaceItem(productUpdated) {
  return httpStore
    .put(`/marketplaces/single_product_item`, { productUpdated })
    .then(res => res.data);
}

export function createMarketplaceItem(newProduct) {
  return httpStore
    .post(`/marketplaces/single_product_item`, { newProduct })
    .then(res => res.data);
}

export function getAllMarketplaceItems(filters) {
  return httpStore
    .get("/marketplaces/reports", { params: filters })
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

export function getHtmlMarketplace(nkrCode) {
  return httpStore
    .get("/marketplaces/marketplaces_html/" + nkrCode)
    .then(res => res.data)
    .catch(e => {
      throw e;
    });
}

// === Amazon SP-API — sincronización real ===

// Sincroniza precio (y stock si FBM) de un solo producto contra Amazon.
export function syncToAmazon(nkrCode) {
  return httpStore
    .post("/marketplaces/amazon_sync_single", { nkrCode })
    .then(res => res.data);
}

// Envía un lote a Amazon (Feeds API). Pasar { codes: [...] } o { filters: {...} }.
// Devuelve { jobId, feedId, totalItems } para hacer polling con getAmazonJob.
export function bulkSyncToAmazon(payload) {
  return httpStore
    .post("/marketplaces/amazon_bulk_sync", payload)
    .then(res => res.data);
}

// Envía a Amazon solo los SKU cuyo precio o stock cambió desde su última
// sincronización. Mismo resultado que bulkSyncToAmazon: { jobId, feedId,
// totalItems }, o { skipped: true } si no había nada que enviar.
export function syncAmazonChanges(payload = {}) {
  return httpStore
    .post("/marketplaces/amazon_sync_changes", payload)
    .then(res => res.data);
}

// Cuántos SKU están pendientes de enviar, sin enviar nada.
export function getAmazonPendingChanges() {
  return httpStore
    .get("/marketplaces/amazon_pending_changes")
    .then(res => res.data);
}

// Estado de un job bulk (feed) por id.
export function getAmazonJob(jobId) {
  return httpStore
    .get("/marketplaces/amazon_job/" + jobId)
    .then(res => res.data);
}

// Estado live del listing en Amazon (+ stock FBA si aplica).
export function getAmazonStatus(nkrCode) {
  return httpStore
    .get("/marketplaces/amazon_status/" + nkrCode)
    .then(res => res.data);
}

// Vista previa de lo que se publicaría (sin llamar a Amazon).
export function getAmazonPreview(nkrCode) {
  return httpStore
    .get("/marketplaces/amazon_preview/" + nkrCode)
    .then(res => res.data);
}

// Busca productTypes de Amazon por palabras clave (selector de categoría).
export function searchAmazonProductTypes(search) {
  return httpStore
    .get("/marketplaces/amazon_product_types", { params: { search } })
    .then(res => res.data);
}

// Productos con stock no publicados en Amazon (pestaña "publicables").
export function getAmazonPublishable(filters) {
  return httpStore
    .get("/marketplaces/amazon_publishable", { params: filters })
    .then(res => res.data);
}

// Reporte de drift: precio/stock local vs vivo en Amazon.
export function getAmazonDrift(filters) {
  return httpStore
    .get("/marketplaces/amazon_drift", { params: filters })
    .then(res => res.data);
}

// Crea/publica el listing en Amazon (PUT). productType/itemId/sku opcionales.
export function createAmazonListing(
  nkrCode,
  {
    productType,
    itemId,
    sku,
    description,
    bullets,
    images,
    attributes,
    gtinExemption
  } = {}
) {
  return httpStore
    .post("/marketplaces/amazon_create", {
      nkrCode,
      productType,
      itemId,
      sku,
      description,
      bullets,
      images,
      attributes,
      gtinExemption
    })
    .then(res => res.data);
}

// === Catálogo Amazon — reconciliación con marketplaces_items ===

// Pide a Amazon el reporte completo de listings (FBA + FBM). Devuelve { jobId }.
export function startAmazonCatalogSync() {
  return httpStore
    .post("/marketplaces/amazon_catalog_sync", {})
    .then(res => res.data);
}

// Estado del reporte de catálogo; al terminar materializa el snapshot local.
export function getAmazonCatalogStatus(jobId) {
  return httpStore
    .get("/marketplaces/amazon_catalog_status", { params: { jobId } })
    .then(res => res.data);
}

// Conteos del cruce Amazon <-> marketplaces_items (tarjetas de la UI).
export function getAmazonReconciliation() {
  return httpStore
    .get("/marketplaces/amazon_reconciliation")
    .then(res => res.data);
}

// Detalle paginado de una categoría del cruce.
// bucket: matched | only_amazon | only_local | channel_mismatch
export function getAmazonReconciliationItems(filters) {
  return httpStore
    .get("/marketplaces/amazon_reconciliation_items", { params: filters })
    .then(res => res.data);
}

// Piezas vendidas en los últimos meses publicadas por FBM y sin listing FBA.
// filters: { days, stock, sort, search, page, limitPerPage }
export function getAmazonFbaOpportunities(filters) {
  return httpStore
    .get("/marketplaces/amazon_fba_opportunities", { params: filters })
    .then(res => res.data);
}

// Crea marketplaces_items para listings que ya existen en Amazon.
// skus omitido => importa todos los importables.
export function importAmazonCatalogItems(skus) {
  return httpStore
    .post("/marketplaces/amazon_catalog_import", { skus })
    .then(res => res.data);
}

// Borra marketplaces_items que ya no existen en Amazon (bucket only_local).
// skus omitido => borra todos los del bucket.
export function deleteAmazonOnlyLocalItems(skus) {
  return httpStore
    .post("/marketplaces/amazon_catalog_delete_local", { skus })
    .then(res => res.data);
}

// Refresca solo el stock FBA del snapshot (sin pedir el reporte de listings).
export function refreshAmazonFbaInventory() {
  return httpStore
    .post("/marketplaces/amazon_fba_inventory", {})
    .then(res => res.data);
}

// === Editor de listing de Amazon ===

// Todo lo necesario para editar un producto: datos nuestros, ficha cacheada de
// Amazon, estado de verificación y bitácora. No consulta a Amazon.
export function getAmazonListingWorkspace(nkrCode) {
  return httpStore
    .get(
      "/marketplaces/amazon_listing_workspace/" + encodeURIComponent(nkrCode)
    )
    .then(res => res.data);
}

// Trae de Amazon la ficha publicada (imágenes, descripción, bullets) y la
// cachea. Es lo único del editor que consume cuota de SP-API.
export function fetchAmazonListingDetails(sku, asin) {
  return httpStore
    .post("/marketplaces/amazon_listing_fetch", { sku, asin })
    .then(res => res.data);
}

// Marca o desmarca el listing como verificado contra Amazon.
export function setAmazonItemVerified(itemId, verified) {
  return httpStore
    .post("/marketplaces/amazon_item_verify", { itemId, verified })
    .then(res => res.data);
}

// Registra en la bitácora una acción cuya escritura vive en otro servicio
// (las imágenes las maneja admin-main-api), para que el historial quede completo.
export function logAmazonItemAction({ nkrCode, sku, action, detail }) {
  return httpStore
    .post("/marketplaces/amazon_item_action", { nkrCode, sku, action, detail })
    .then(res => res.data);
}

// Bitácora global: qué se hizo, sobre qué producto y quién lo hizo.
export function getAmazonItemActions(filters) {
  return httpStore
    .get("/marketplaces/amazon_actions", { params: filters })
    .then(res => res.data);
}

// Qué cambiaría en Amazon con este contenido, sin enviar nada. Es el paso
// previo del envío: la confirmación muestra este diff.
export function previewAmazonContent(payload) {
  return httpStore
    .post("/marketplaces/amazon_content_preview", payload)
    .then(res => res.data);
}

// Envía título, bullets, descripción y/o imágenes a un listing existente (PATCH).
// Las claves ausentes no se tocan en Amazon.
export function pushAmazonContent(payload) {
  return httpStore
    .post("/marketplaces/amazon_content_push", payload)
    .then(res => res.data);
}

// === A+ Content ===

// Elegibilidad de A+: requiere Brand Registry y el rol de A+ autorizado en la
// app de SP-API. Son dos permisos distintos.
export function getAmazonAplusStatus() {
  return httpStore
    .get("/marketplaces/amazon_aplus_status")
    .then(res => res.data);
}

// Estado de A+ de un producto: ASIN, documentos que hoy los cubren y un
// borrador sugerido con sus specs y compatibilidad.
export function getAmazonAplusForProduct(nkrCode) {
  return httpStore
    .get("/marketplaces/amazon_aplus_product/" + encodeURIComponent(nkrCode))
    .then(res => res.data);
}

// Guarda el documento A+ y lo asocia a los ASIN. submit = true lo manda además
// a revisión de Amazon.
export function saveAmazonAplus(payload) {
  return httpStore
    .post("/marketplaces/amazon_aplus_save", payload)
    .then(res => res.data);
}

// Contenido real de un documento A+ existente, para editarlo en vez de
// reemplazarlo con el borrador generado.
export function getAmazonAplusDocument(contentReferenceKey) {
  return httpStore
    .get(
      "/marketplaces/amazon_aplus_document/" +
        encodeURIComponent(contentReferenceKey)
    )
    .then(res => res.data);
}

// Guarda el borrador local de A+ (lo que el panel tiene a medio editar).
// Sin `draft` lo descarta. No toca Amazon.
export function saveAmazonAplusDraft(nkrCode, draft) {
  return httpStore
    .post("/marketplaces/amazon_aplus_draft", { nkrCode, draft })
    .then(res => res.data);
}

// Tandas de sugerencias del modelo para un producto, la más nueva primero.
export function getAmazonAplusSuggestions(nkrCode) {
  return httpStore
    .get(
      "/marketplaces/amazon_aplus_suggestions/" + encodeURIComponent(nkrCode)
    )
    .then(res => res.data);
}
